import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { defs } from '../../content/registry';
import type { FactionId } from '../../content/types';
import {
  IMPORTED_MODEL_ASSETS,
  ONLINE_MODEL_CANDIDATES,
  loadImportedModel,
  type ImportedModelAsset,
} from '../../render/modelAssets';
import {
  createBuildingPreview,
  createUnitPreview,
  type PreviewModel,
  type PreviewMotion,
} from '../../render/modelPreviewRigs';
import { Select } from '../components/Select';

type ModelKind = 'squads' | 'buildings' | 'imported';
type FactionFilter = 'all' | FactionId;

const FACTIONS: { id: FactionFilter; label: string }[] = [
  { id: 'all', label: 'All Factions' },
  { id: 'iron-covenant', label: 'Iron Covenant' },
  { id: 'verdant-wilds', label: 'Verdant Wilds' },
  { id: 'ashen-host', label: 'Ashen Host' },
  { id: 'astral-dominion', label: 'Astral Dominion' },
];

function disposeSceneObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.geometry.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    for (const material of materials) material.dispose();
  });
}

function ImportedModelStage({ asset, paused }: { asset: ImportedModelAsset; paused: boolean }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(paused);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let disposed = false;
    setState('loading');

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.08;
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0c1110);
    const environment = new RoomEnvironment();
    const pmrem = new THREE.PMREMGenerator(renderer);
    const environmentMap = pmrem.fromScene(environment, 0.04).texture;
    scene.environment = environmentMap;
    environment.dispose();
    pmrem.dispose();
    scene.add(new THREE.HemisphereLight(0xd9edcf, 0x211b16, 1.2));
    const key = new THREE.DirectionalLight(0xffe1ae, 2.5);
    key.position.set(6, 9, 7);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    scene.add(key);

    const camera = new THREE.PerspectiveCamera(38, 1, 0.03, 100);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.maxPolarAngle = Math.PI * 0.49;
    let model: THREE.Group | undefined;
    let floor: THREE.Mesh | undefined;

    void loadImportedModel(asset).then((loaded) => {
      if (disposed) {
        disposeSceneObject(loaded);
        return;
      }
      model = loaded;
      scene.add(model);
      const bounds = new THREE.Box3().setFromObject(model);
      const size = bounds.getSize(new THREE.Vector3());
      const center = bounds.getCenter(new THREE.Vector3());
      const radius = Math.max(1.3, size.length() * 0.58);
      controls.target.copy(center);
      camera.position.set(center.x + radius * 1.1, center.y + radius * 0.72, center.z + radius * 1.35);
      camera.near = Math.max(0.02, radius / 100);
      camera.far = Math.max(100, radius * 20);
      camera.updateProjectionMatrix();
      controls.minDistance = radius * 0.38;
      controls.maxDistance = radius * 5;
      controls.update();

      floor = new THREE.Mesh(
        new THREE.CircleGeometry(Math.max(3.5, radius * 1.15), 64),
        new THREE.MeshStandardMaterial({ color: 0x263321, roughness: 0.96 }),
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = bounds.min.y - 0.02;
      floor.receiveShadow = true;
      scene.add(floor);
      setState('ready');
    }).catch((error: unknown) => {
      console.error('Unable to load Art Lab model', error);
      if (!disposed) setState('error');
    });

    const resize = () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();
    let raf = 0;
    let previous = performance.now();
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const delta = Math.min(0.05, (now - previous) / 1000);
      previous = now;
      if (model && !pausedRef.current) model.rotation.y += delta * 0.18;
      controls.update();
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      observer.disconnect();
      controls.dispose();
      if (model) disposeSceneObject(model);
      if (floor) disposeSceneObject(floor);
      environmentMap.dispose();
      renderer.dispose();
      host.removeChild(renderer.domElement);
    };
  }, [asset]);

  return (
    <div className="modelviewer-stage artlabstage" ref={hostRef} aria-label={`Rotatable imported model preview of ${asset.name}`}>
      <div className="modelviewer-hint">Drag to rotate · wheel to zoom · production-scale normalization</div>
      {state !== 'ready' && <div className={`artlabload ${state}`}>{state === 'loading' ? 'Loading audited asset…' : 'Asset could not be loaded'}</div>}
    </div>
  );
}

export function ModelViewer({ onBack }: { onBack: () => void }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const motionRef = useRef<PreviewMotion>('walk');
  const pausedRef = useRef(false);
  const [kind, setKind] = useState<ModelKind>('squads');
  const [faction, setFaction] = useState<FactionFilter>('all');
  const [selectedId, setSelectedId] = useState('u-vw-razorhorn');
  const [motion, setMotion] = useState<PreviewMotion>('walk');
  const [paused, setPaused] = useState(false);
  const [owner, setOwner] = useState<0 | 1>(0);
  const [showSquad, setShowSquad] = useState(true);

  const options = useMemo(() => {
    if (kind === 'imported') return IMPORTED_MODEL_ASSETS;
    const source = kind === 'squads' ? [...defs.units.values()] : [...defs.buildings.values()];
    return source
      .filter((d) => faction === 'all' || d.faction === faction)
      .sort((a, b) => a.faction.localeCompare(b.faction) || a.name.localeCompare(b.name));
  }, [kind, faction]);

  useEffect(() => {
    if (!options.some((d) => d.id === selectedId) && options[0]) setSelectedId(options[0].id);
  }, [options, selectedId]);
  useEffect(() => { motionRef.current = motion; }, [motion]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const selectedImported = kind === 'imported'
    ? IMPORTED_MODEL_ASSETS.find((asset) => asset.id === selectedId)
    : undefined;
  const selected = kind === 'squads'
    ? defs.units.get(selectedId)
    : kind === 'buildings' ? defs.buildings.get(selectedId) : selectedImported;
  const selectedUnit = kind === 'squads' ? defs.units.get(selectedId) : undefined;

  useEffect(() => {
    const host = hostRef.current;
    if (kind === 'imported') return;
    const def = kind === 'squads' ? defs.units.get(selectedId) : defs.buildings.get(selectedId);
    if (!host || !def) return;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0c1110);
    const environment = new RoomEnvironment();
    const pmrem = new THREE.PMREMGenerator(renderer);
    const environmentMap = pmrem.fromScene(environment, 0.04).texture;
    scene.environment = environmentMap;
    environment.dispose();
    pmrem.dispose();
    scene.add(new THREE.HemisphereLight(0xd9edcf, 0x261d15, 1.65));
    const key = new THREE.DirectionalLight(0xffe1ae, 3.1);
    key.position.set(6, 9, 7);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x6fa46b, 1.4);
    rim.position.set(-5, 4, -5);
    scene.add(rim);

    const camera = new THREE.PerspectiveCamera(38, 1, 0.05, 100);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.minDistance = 1;
    controls.maxDistance = 50;
    controls.maxPolarAngle = Math.PI * 0.49;

    const models: PreviewModel[] = [];
    const unitDef = kind === 'squads' ? defs.units.get(selectedId) : undefined;
    const memberCount = unitDef && showSquad && !unitDef.isBoss ? unitDef.squadMemberCount : 1;
    const columns = Math.max(1, Math.ceil(Math.sqrt(memberCount)));
    for (let i = 0; i < memberCount; i++) {
      const model = kind === 'squads'
        ? createUnitPreview(selectedId, owner, i + 1)
        : createBuildingPreview(selectedId, owner);
      scene.add(model.group);
      models.push(model);
    }

    // Derive formation spacing from the actual rendered geometry. A single scalar based on UI
    // scale made long quadrupeds overlap even though ordinary infantry looked correctly spaced.
    const memberSize = new THREE.Vector3(1, 1, 1);
    if (models[0]) new THREE.Box3().setFromObject(models[0].group).getSize(memberSize);
    const spacingX = Math.max(1.2, memberSize.x + Math.max(0.35, memberSize.x * 0.16));
    const spacingZ = Math.max(1.2, memberSize.z + Math.max(0.45, memberSize.z * 0.18));
    const rows = Math.ceil(memberCount / columns);
    models.forEach((model, i) => {
      const row = Math.floor(i / columns);
      const col = i % columns;
      const columnsInRow = Math.min(columns, memberCount - row * columns);
      model.group.position.x = (col - (columnsInRow - 1) / 2) * spacingX;
      model.group.position.z = (row - (rows - 1) / 2) * spacingZ;
    });

    const bounds = new THREE.Box3();
    for (const model of models) bounds.expandByObject(model.group);
    const size = bounds.getSize(new THREE.Vector3());
    const center = bounds.getCenter(new THREE.Vector3());
    const radius = Math.max(1.5, size.length() * 0.56);
    controls.target.copy(center);
    camera.position.set(center.x + radius * 1.15, center.y + radius * 0.72, center.z + radius * 1.35);
    camera.near = Math.max(0.03, radius / 100);
    camera.far = Math.max(100, radius * 20);
    camera.updateProjectionMatrix();
    controls.minDistance = radius * 0.45;
    controls.maxDistance = radius * 5;
    controls.update();

    const floorRadius = Math.max(4, radius * 1.15);
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(floorRadius, 64),
      new THREE.MeshStandardMaterial({ color: 0x263321, roughness: 1, metalness: 0 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = Math.min(0, bounds.min.y) - 0.02;
    floor.receiveShadow = true;
    scene.add(floor);
    const floorRing = new THREE.Mesh(
      new THREE.RingGeometry(floorRadius * 0.88, floorRadius * 0.895, 64),
      new THREE.MeshBasicMaterial({ color: 0x8aa05a, transparent: true, opacity: 0.35 }),
    );
    floorRing.rotation.x = -Math.PI / 2;
    floorRing.position.y = floor.position.y + 0.006;
    scene.add(floorRing);

    const resize = () => {
      const w = Math.max(1, host.clientWidth);
      const h = Math.max(1, host.clientHeight);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();

    let raf = 0;
    let elapsed = 0;
    let previous = performance.now();
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - previous) / 1000);
      previous = now;
      if (!pausedRef.current) elapsed += dt;
      for (const model of models) model.animate(motionRef.current, elapsed, pausedRef.current ? 0 : dt);
      controls.update();
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      controls.dispose();
      scene.traverse((o) => {
        if (!(o instanceof THREE.Mesh)) return;
        o.geometry.dispose();
        const materials = Array.isArray(o.material) ? o.material : [o.material];
        for (const material of materials) material.dispose();
      });
      environmentMap.dispose();
      renderer.dispose();
      host.removeChild(renderer.domElement);
    };
  }, [kind, selectedId, owner, showSquad]);

  const chooseKind = (next: ModelKind) => {
    setKind(next);
    if (next === 'imported') {
      setSelectedId(IMPORTED_MODEL_ASSETS[0]?.id ?? '');
      return;
    }
    const source = next === 'squads' ? [...defs.units.values()] : [...defs.buildings.values()];
    const first = source.find((d) => faction === 'all' || d.faction === faction) ?? source[0];
    if (first) setSelectedId(first.id);
  };

  return (
    <div className="modelviewer-screen">
      <header className="modelviewer-head">
        <div>
          <div className="modelviewer-kicker">Developer Tools · Production Model Inspector &amp; Art Lab</div>
          <h1>{selected?.name ?? 'Model Viewer'}</h1>
        </div>
        <button className="btn" onClick={onBack}>Back to Settings</button>
      </header>
      <div className="modelviewer-toolbar">
        <label><span>Content</span><Select ariaLabel="Content" value={kind} options={[{ value: 'squads' as ModelKind, label: 'Squads' }, { value: 'buildings' as ModelKind, label: 'Buildings' }, { value: 'imported' as ModelKind, label: 'Imported Assets' }]} onChange={chooseKind} /></label>
        <label><span>Faction</span><Select ariaLabel="Faction" value={faction} disabled={kind === 'imported'} options={FACTIONS.map((f) => ({ value: f.id, label: f.label }))} onChange={(id) => setFaction(id as FactionFilter)} /></label>
        <label className="modelviewer-modelselect"><span>Model</span><Select ariaLabel="Model" value={selectedId} options={options.map((d) => ({ value: d.id, label: d.name }))} onChange={setSelectedId} /></label>
      </div>
      {kind === 'imported' && selectedImported
        ? <ImportedModelStage asset={selectedImported} paused={paused} />
        : <div className="modelviewer-stage" ref={hostRef} aria-label={`Rotatable animated 3D preview of ${selected?.name ?? 'selected model'}`}>
          <div className="modelviewer-hint">Drag to rotate · wheel to zoom · right-drag to pan</div>
        </div>}
      <div className="modelviewer-controls">
        <div className="modelviewer-meta">
          {selected && 'faction' in selected && <span>{selected.faction.replaceAll('-', ' ')}</span>}
          {selectedImported && <span>{selectedImported.category}</span>}
          <code>{selected?.id}</code>
          {selectedUnit?.workerVariant
            ? <span>{selectedId === 'u-vw-barkwright' ? '4-druid rig' : 'specialist worker'}</span>
            : selectedUnit?.squadMemberCount !== undefined && <span>{selectedUnit.squadMemberCount} members</span>}
        </div>
        <label><span>Animation</span><Select ariaLabel="Animation" value={motion} disabled={kind !== 'squads'} options={[{ value: 'idle' as PreviewMotion, label: 'Idle' }, { value: 'walk' as PreviewMotion, label: 'Walk' }, { value: 'charge' as PreviewMotion, label: 'Charge' }, { value: 'attack' as PreviewMotion, label: 'Attack' }]} onChange={setMotion} /></label>
        <div className="modelviewer-buttongroup" aria-label="Preview options">
          <button className={`btn small${paused ? ' toggled' : ''}`} onClick={() => setPaused((v) => !v)}>{paused ? 'Resume' : 'Pause'}</button>
          {kind === 'squads' && <button className={`btn small${showSquad ? ' toggled' : ''}`} onClick={() => setShowSquad((v) => !v)}>{showSquad ? 'Full Squad' : 'Single Member'}</button>}
          {kind !== 'imported' && <button className="btn small" onClick={() => setOwner((v) => (v === 0 ? 1 : 0))}>Team {owner === 0 ? 'Blue' : 'Orange'}</button>}
        </div>
      </div>
      {selectedImported && <aside className="artlabasset">
        <div><strong>{selectedImported.status}</strong><span>{selectedImported.use}</span></div>
        <div><span>{selectedImported.creator} · {selectedImported.license}</span><a href={selectedImported.sourceUrl} target="_blank" rel="noreferrer">Open source page ↗</a></div>
      </aside>}
      {kind === 'imported' && <details className="artlabcandidates">
        <summary>Reviewed character replacement candidates ({ONLINE_MODEL_CANDIDATES.length})</summary>
        <div className="artlabcandidategrid">{ONLINE_MODEL_CANDIDATES.map((candidate) => <article key={candidate.name}>
          <h3>{candidate.name}</h3>
          <div>{candidate.creator} · {candidate.license}</div>
          <strong>{candidate.bestFor}</strong>
          <p>{candidate.assessment}</p>
          <a href={candidate.sourceUrl} target="_blank" rel="noreferrer">Review candidate ↗</a>
        </article>)}</div>
      </details>}
    </div>
  );
}
